import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Lock, Unlock, ExternalLink } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation';
import { request, useFocusState } from './focusFetch';
import { defaultSelection, byVotes } from './selection';

const Wrap = styled.section`
  padding: 24px 32px; display: flex; flex-direction: column; gap: 14px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  @media (max-width: 640px) { padding: 20px 16px; }
  h3 { margin: 0; font-size: 14px; font-weight: 600; color: rgba(255,255,255,0.75); text-transform: uppercase; letter-spacing: 0.06em; }
  p { margin: 0; line-height: 1.5; }
`;
const Muted = styled.p`color: rgba(255,255,255,0.55); font-size: 13px;`;
const Row = styled.label`
  display: grid; grid-template-columns: auto 1fr auto; gap: 10px; align-items: center;
  padding: 8px 10px; border-radius: 8px; border: 1px solid #2c3444; cursor: pointer; font-size: 14px;
  input { width: 18px; height: 18px; }
  b { font-variant-numeric: tabular-nums; }
`;
const ErrorBox = styled.p`
  padding: 8px 12px; border-radius: 8px; font-size: 13px;
  border: 1px solid rgba(255,107,107,0.5); background: rgba(255,107,107,0.12); color: #ffd9d9;
`;
const Reason = styled.textarea`
  width: 100%; box-sizing: border-box; min-height: 60px; padding: 8px 10px; border-radius: 8px;
  border: 1px solid #2c3444; background: #0f131a; color: #fff; font: inherit; font-size: 14px;
`;
const Actions = styled.div`display: flex; flex-wrap: wrap; gap: 10px; align-items: center;`;
const Button = styled.button`
  all: unset; cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px; border-radius: 8px; font-size: 13px; font-weight: 600;
  color: ${(p) => (p.$primary ? '#0b0d12' : 'rgba(255,255,255,0.85)')};
  background: ${(p) => (p.$primary ? '#c9a7ff' : 'transparent')};
  border: 1px solid ${(p) => (p.$primary ? '#c9a7ff' : '#2c3444')};
  &:disabled { opacity: 0.45; cursor: default; }
`;

/**
 * The facilitator's panel for Vipupisteiden valinta in /admin/workshop: the
 * vote standings and the lock (specs/workshop-modules/vipupiste_focus.md). The
 * ticks start from the vote; the facilitator can change any of them. Locking
 * closes the vote; reopening it asks why.
 */
export default function VipupisteFocusAdmin({ instanceId, locked }) {
  const { t } = useTranslation();
  const { state, error: loadError, reload } = useFocusState(instanceId);
  const [chosen, setChosen] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const vipupisteet = state?.vipupisteet || [];
  const counts = state?.counts || {};
  const ranked = useMemo(() => byVotes(vipupisteet, counts), [vipupisteet, counts]);

  // Start from the vote, or from the lock when there is one; keep the facilitator's edits after that.
  useEffect(() => {
    if (state && chosen === null) {
      setChosen(state.lock ? state.lock.selectedIds : defaultSelection(vipupisteet.map((v) => v.id), counts, state.mode));
    }
  }, [state, chosen, vipupisteet, counts]);

  if (loadError) return <Wrap><ErrorBox role="alert">{t('vipupisteFocus.loadError', { message: loadError.message })}</ErrorBox></Wrap>;
  if (!state || chosen === null) return <Wrap><Muted>{t('vipupisteFocus.loading')}</Muted></Wrap>;

  const isLocked = Boolean(state.lock);
  const selected = new Set(chosen);
  const toggle = (id) => setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const fromVote = () => setChosen(defaultSelection(vipupisteet.map((v) => v.id), counts, state.mode));

  const run = async (url, body, after) => {
    setBusy(true);
    setError(null);
    try {
      await request(url, { method: 'POST', body });
      after?.();
      await reload();
    } catch (e) {
      setError(t('vipupisteFocus.admin.failed', { message: e.message }));
    } finally {
      setBusy(false);
    }
  };
  const lock = () => run(API_ENDPOINTS.VIPUPISTE_FOCUS_LOCK(instanceId), { selectedIds: chosen });
  const unlock = () => run(API_ENDPOINTS.VIPUPISTE_FOCUS_UNLOCK(instanceId), { reason }, () => setReason(''));

  return (
    <Wrap>
      <h3>{t('vipupisteFocus.admin.title')}</h3>
      <Muted>
        {t(state.mode === 'prune' ? 'vipupisteFocus.admin.modePrune' : 'vipupisteFocus.admin.modeKeep', { budget: state.budget })}
        {' '}
        {t(isLocked ? 'vipupisteFocus.admin.closed' : 'vipupisteFocus.admin.open')}
      </Muted>
      {!vipupisteet.length && <Muted>{t('vipupisteFocus.empty')}</Muted>}

      {ranked.map((v) => (
        <Row key={v.id}>
          <input type="checkbox" checked={selected.has(v.id)} onChange={() => toggle(v.id)} disabled={locked || isLocked} />
          <span>{v.title}</span>
          <b>{counts[v.id] || 0}</b>
        </Row>
      ))}

      {isLocked && (
        <>
          <Muted>{t('vipupisteFocus.admin.unlockHint')}</Muted>
          <Reason value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('vipupisteFocus.admin.unlockPlaceholder')} />
        </>
      )}
      {error && <ErrorBox role="alert">{error}</ErrorBox>}

      <Actions>
        {isLocked ? (
          <Button type="button" onClick={unlock} disabled={locked || busy || !reason.trim()}>
            <Unlock size={14} aria-hidden />
            {t('vipupisteFocus.admin.unlock')}
          </Button>
        ) : (
          <>
            <Button type="button" $primary onClick={lock} disabled={locked || busy || !chosen.length}>
              <Lock size={14} aria-hidden />
              {t('vipupisteFocus.admin.lock', { count: chosen.length, total: vipupisteet.length })}
            </Button>
            <Button type="button" onClick={fromVote} disabled={locked || busy}>
              {t('vipupisteFocus.admin.fromVote')}
            </Button>
          </>
        )}
        <Button as="a" href={`/vipupisteiden-valinta?kid=${instanceId}`} target="_blank" rel="noreferrer">
          <ExternalLink size={14} aria-hidden />
          {t('vipupisteFocus.admin.openModule')}
        </Button>
      </Actions>
    </Wrap>
  );
}
